import { Box, Button, IconButton, Snackbar } from '@material-ui/core';
import Alert, { Color } from '@material-ui/lab/Alert';
import { formatDateTimeReadable } from '@nemuru-eng/js-api-client';

import { Font } from '@/components/shared/components/Font';
import { NotificationSnackbarError } from '@/components/shared/components/NotificationManager';
import { useNotificationSnackbar } from '@/components/shared/components/NotificationSnackbar/hooks/useNotificationSnackbar';
import { useStyles } from '@/components/shared/components/NotificationSnackbar/hooks/useStyles';
import { Text } from '@/components/shared/components/Text';

interface NotificationSnackbarProps {
  open?: boolean;
  autoHide?: boolean;
  onClose?: (event?, reason?) => void;
  message?: any;
  subMessage?: any;
  errorObject?: NotificationSnackbarError | null;
  level?: Color;
  style?: React.CSSProperties;
  className?: string;
}

export function NotificationSnackbar({
  open,
  autoHide = false,
  onClose,
  message,
  subMessage,
  errorObject,
  level,
  ...props
}: NotificationSnackbarProps) {
  const { errorDetails, allowShowMoreButton, showMoreState, setShowMoreState } = useNotificationSnackbar(errorObject);
  const classes = useStyles();

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHide ? 5000 : 999999999}
      onClose={
        autoHide && onClose
          ? () => onClose()
          : () => {
              return null;
            }
      }
      message={!level && message}
      className={classes.root}
      {...props}
    >
      {level && (
        <Alert
          style={{ width: '100%', maxWidth: '800px' }}
          severity={level}
          action={
            !autoHide &&
            onClose && (
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => onClose()}>
                <i className="material-icons-outlined" style={{ verticalAlign: 'text-top', fontSize: '24px' }}>
                  close
                </i>
              </IconButton>
            )
          }
        >
          <div>
            <strong>{message}</strong>
          </div>
          {subMessage && <small style={{ wordBreak: 'break-all' }}>{subMessage}</small>}
          {level === 'error' && errorDetails && (
            <Box display={'flex'} flexDirection={'column'}>
              {errorDetails.errorSubmessage && <small>{errorDetails.errorSubmessage}</small>}
              {errorDetails.date && (
                <small>{formatDateTimeReadable(errorDetails.date, undefined, { second: 'numeric' })}</small>
              )}
              <small>
                {(errorDetails.errorTitle || errorDetails.detailsBody) && (
                  <Box>
                    <Box py={1}>
                      <strong>{errorDetails.errorTitle}:</strong>
                    </Box>
                    {errorDetails.detailsBody && (
                      <Box
                        id={'details-body'}
                        className={showMoreState ? classes.root : `${classes.root} line-clamp-3 overflow-text-ellipsis`}
                        style={{
                          whiteSpace: 'break-spaces',
                          textAlign: 'initial',
                          maxWidth: '100%',
                          marginBottom: allowShowMoreButton ? '28px' : 0,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {errorDetails.detailsBody}
                      </Box>
                    )}
                  </Box>
                )}
              </small>
              {allowShowMoreButton && (
                <Box position={'absolute'} bottom={'8px'} right={'8px'}>
                  <Button
                    size={'small'}
                    onClick={() => setShowMoreState(!showMoreState)}
                    style={{ maxWidth: 'fit-content' }}
                  >
                    <Font variant={'caption'}>
                      <Text tId={showMoreState ? 'SHOW_LESS' : 'SHOW_MORE'} />
                    </Font>
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Alert>
      )}
    </Snackbar>
  );
}
